/**
 * useLiveRegion Hook
 * 
 * Manages ARIA live regions for screen reader announcements.
 * Provides polite and assertive announcements without visual changes.
 */

import { useEffect, useRef, useCallback } from 'react';

export type LiveRegionPoliteness = 'polite' | 'assertive' | 'off';

export interface LiveRegionConfig {
  /**
   * Politeness level
   * @default 'polite'
   */
  politeness?: LiveRegionPoliteness;
  
  /**
   * Clear announcement after delay (ms)
   * @default 1000
   */
  clearDelay?: number;
  
  /**
   * Atomic (announce all content at once)
   * @default true
   */
  atomic?: boolean;
}

/**
 * Live region hook for screen reader announcements
 * 
 * @example
 * ```tsx
 * function SearchResults({ results, isLoading }) {
 *   const announce = useLiveRegion();
 *   
 *   useEffect(() => {
 *     if (!isLoading) {
 *       announce(`${results.length} resultados encontrados`);
 *     }
 *   }, [results, isLoading]);
 *   
 *   return <ResultsList results={results} />;
 * }
 * ```
 */
export function useLiveRegion(config: LiveRegionConfig = {}) {
  const {
    politeness = 'polite',
    clearDelay = 1000,
    atomic = true,
  } = config;
  
  const regionRef = useRef<HTMLDivElement | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();
  
  // Create live region on mount
  useEffect(() => {
    const region = document.createElement('div');
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', politeness);
    region.setAttribute('aria-atomic', String(atomic));
    region.className = 'sr-only'; // Visually hidden
    
    document.body.appendChild(region);
    regionRef.current = region;
    
    return () => {
      if (regionRef.current) {
        document.body.removeChild(regionRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [politeness, atomic]);
  
  // Announce message
  const announce = useCallback((message: string, customDelay?: number) => {
    if (!regionRef.current) return;
    
    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Set message
    regionRef.current.textContent = message;
    
    // Clear after delay
    const delay = customDelay ?? clearDelay;
    if (delay > 0) {
      timeoutRef.current = setTimeout(() => {
        if (regionRef.current) {
          regionRef.current.textContent = '';
        }
      }, delay);
    }
  }, [clearDelay]);
  
  return announce;
}

/**
 * Global live region manager
 * 
 * Singleton for app-wide announcements
 */
class LiveRegionManager {
  private politeRegion: HTMLDivElement | null = null;
  private assertiveRegion: HTMLDivElement | null = null;
  
  initialize() {
    if (this.politeRegion && this.assertiveRegion) return;
    
    // Polite region
    this.politeRegion = document.createElement('div');
    this.politeRegion.setAttribute('role', 'status');
    this.politeRegion.setAttribute('aria-live', 'polite');
    this.politeRegion.setAttribute('aria-atomic', 'true');
    this.politeRegion.className = 'sr-only';
    document.body.appendChild(this.politeRegion);
    
    // Assertive region
    this.assertiveRegion = document.createElement('div');
    this.assertiveRegion.setAttribute('role', 'alert');
    this.assertiveRegion.setAttribute('aria-live', 'assertive');
    this.assertiveRegion.setAttribute('aria-atomic', 'true');
    this.assertiveRegion.className = 'sr-only';
    document.body.appendChild(this.assertiveRegion);
  }
  
  announce(message: string, politeness: 'polite' | 'assertive' = 'polite') {
    this.initialize();
    
    const region = politeness === 'polite' ? this.politeRegion : this.assertiveRegion;
    if (!region) return;
    
    // Clear and set message
    region.textContent = '';
    setTimeout(() => {
      region!.textContent = message;
    }, 100);
    
    // Clear after 1 second
    setTimeout(() => {
      region!.textContent = '';
    }, 1100);
  }
}

export const liveRegion = new LiveRegionManager();

/**
 * Initialize global live regions
 * Call in app entry point
 */
export function initializeLiveRegions() {
  liveRegion.initialize();
}

